import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarCheck } from '@phosphor-icons/react';
import { Funnel, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Skeleton } from '../components/ui/skeleton';
import { TYPE_ELEMENT, MENU } from '../lib/libelles';
import MaSemaineCabinet from '../components/dashboards/MaSemaineCabinet';
import MaSemaineBloc from '../components/dashboards/MaSemaineBloc';
import LotsPartenaire from '../components/dashboards/LotsPartenaire';
import MesVisasCard from '../components/project/visas/MesVisasCard';
import { URL_MA_SEMAINE, dateCourte } from '../components/dashboards/maSemaine';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RP04 — libellés du vocabulaire unique (lib/libelles.js).
const TYPE_LABELS = TYPE_ELEMENT;

const PRIORITE_COLORS = {
  urgent: 'bg-red-100 text-red-700 border-red-300',
  normal: 'bg-blue-50 text-blue-700 border-blue-200',
  basse: 'bg-gray-100 text-gray-600 border-gray-300',
};
const PRIORITE_LABELS = { urgent: 'Urgente', normal: 'Normale', basse: 'Basse' };

/**
 * Fluidité B2 — « Ma semaine » : UNE seule page (menu de gauche → « Ma semaine »).
 *
 * Avant : « Mes actions » (menu) était vide pour l'architecte, le collaborateur et le BET
 * (« Aucune action assignée 🎉 ») et perdait le menu de gauche (impasse), pendant que
 * « Ma semaine » du tableau de bord montrait le CR à relire, la levée à vérifier et
 * l'action en retard (P3#22, P1#14, P1#24).
 *
 * Maintenant, dans le cadre commun (Layout, menu de gauche toujours là) :
 *  - architecte, chef de projet, collaborateur : exactement ce que montre le tableau de
 *    bord (MaSemaineCabinet, GET /api/me/ma-semaine), dont les désaccords des
 *    entreprises, avec « Relancer » (D2 : préparer, ouvert au collaborateur) et le statut
 *    des lots à rédiger ;
 *  - partenaire : pièces à viser, lots à viser / à rédiger, réunions de la semaine ;
 *  - puis « Mes actions » (GET /api/me/tasks) seulement s'il y en a : plus de bloc vide.
 * L'ancienne adresse /mes-taches redirige ici (App.js).
 */
export default function MesTaches() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterChantier, setFilterChantier] = useState('');
  const [filterEcheance, setFilterEcheance] = useState('all');
  const [semaine, setSemaine] = useState(null);
  const [semaineLoading, setSemaineLoading] = useState(true);
  const [relancees, setRelancees] = useState({});

  useEffect(() => {
    // A6 — « Ma semaine » est réservée au cabinet et aux partenaires : pas de message
    // rouge pour les autres rôles (client, AMO, entreprise).
    axios.get(`${API_URL}${URL_MA_SEMAINE}`, { withCredentials: true, silent403: true })
      .then(({ data }) => setSemaine(data))
      .catch(() => setSemaine(null))
      .finally(() => setSemaineLoading(false));
  }, []);

  // D2 — relancer une entreprise = préparer : ouvert au collaborateur (POST existant).
  const relancer = async (r) => {
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${r.chantier_id}/dce/${r.consultation_id}/relancer/${r.id}`,
        {}, { withCredentials: true },
      );
      setRelancees((m) => ({ ...m, [r.id]: true }));
      toast.success(`Relance envoyée à ${r.entreprise}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "La relance n'a pas pu être envoyée");
    }
  };

  const partenaire = semaine?.profil === 'partenaire';
  const cabinet = !!semaine && !partenaire; // architecte, chef de projet ou collaborateur

  // Boutons ajoutés aux lignes de « Ma semaine » (collaborateur : relances, statut des lots)
  const actionsSemaine = {
    relances: (r) => (r.type === 'consultation' && r.consultation_id ? (
      <button
        type="button"
        disabled={!!relancees[r.id]}
        onClick={() => relancer(r)}
        className="text-xs px-2 py-1 border border-[#C75B39] text-[#C75B39] hover:bg-[#FDF6EC] disabled:opacity-50 flex-shrink-0"
        data-testid={`ma-semaine-relancer-${r.id}`}
      >
        {relancees[r.id] ? 'Relancée' : 'Relancer'}
      </button>
    ) : null),
    lots_confies: (x) => (
      <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 flex-shrink-0 ${
        x.statut === 'a_relire' ? 'bg-[#FFF4E5] text-[#8A5A00]' : x.statut === 'valide' ? 'bg-[#E8F5E9] text-[#2D6A4F]' : 'bg-[#F5F5F3] text-[#4A4A4A]'
      }`}>
        {x.statut_libelle}
      </span>
    ),
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/me/tasks?etat=active`, { withCredentials: true, silent403: true });
        setTasks(data?.tasks || []);
      } catch (e) {
        if (e.response?.status !== 403) toast.error('Erreur de chargement des actions');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const chantiersUnique = useMemo(
    () => [...new Set(tasks.map(t => t.chantier_name).filter(Boolean))].sort(),
    [tasks],
  );

  const filtered = useMemo(() => {
    let result = tasks;
    if (filterChantier) result = result.filter(t => t.chantier_name === filterChantier);
    if (filterEcheance !== 'all') {
      const now = new Date();
      const limit =
        filterEcheance === 'overdue' ? now :
        filterEcheance === 'week' ? new Date(now.getTime() + 7 * 86400000) :
        new Date(now.getTime() + 30 * 86400000);
      result = result.filter(t => {
        if (!t.echeance) return false;
        const e = new Date(t.echeance);
        return filterEcheance === 'overdue' ? e < now : e <= limit;
      });
    }
    return result;
  }, [tasks, filterChantier, filterEcheance]);

  const toutCharge = !loading && !semaineLoading;
  const rienDuTout = toutCharge && tasks.length === 0 && !cabinet && !partenaire;

  return (
    <Layout>
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="page-ma-semaine">
        <div className="flex items-center gap-3 mb-6" data-testid="page-mes-taches">
          <CalendarCheck size={28} className="text-[#C75B39]" />
          <div>
            <h1 className="text-2xl text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>{MENU.maSemaine}</h1>
            <p className="text-sm text-[#6B6B6B]">
              {semaine
                ? `Du ${dateCourte(semaine.aujourd_hui)} au ${dateCourte(semaine.fin_semaine)} — ce qui vous attend sur vos chantiers`
                : 'Ce qui vous attend sur vos chantiers'}
            </p>
          </div>
        </div>

        {/* Architecte, chef de projet, collaborateur : la même « Ma semaine » que le tableau de bord */}
        {(semaineLoading || cabinet) && (
          <MaSemaineCabinet semaine={cabinet ? semaine : null} loading={semaineLoading} actions={actionsSemaine} sansTitre max={10} />
        )}

        {/* Partenaire (BET, contrôleur technique, SPS, OPC…) */}
        {partenaire && (
          <section className="space-y-6 mb-8" data-testid="ma-semaine-partenaire">
            <MesVisasCard />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <LotsPartenaire mesLots={semaine.mes_lots || []} />
              <MaSemaineBloc cle="reunions" items={semaine.reunions || []} total={semaine.compteurs?.reunions} vide="Aucune réunion cette semaine" />
            </div>
          </section>
        )}

        {/* Mes actions : seulement s'il y en a (plus de page « Aucune action assignée »). */}
        {loading ? (
          !semaineLoading && !cabinet && !partenaire && (
            <div className="space-y-3" data-testid="mes-taches-loading">
              {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          )
        ) : tasks.length > 0 && (
          <section data-testid="mes-actions">
            <h2 className="text-lg text-[#2C2C2C] mb-1" style={{ fontFamily: 'DM Serif Display, serif' }}>Mes actions</h2>
            <p className="text-sm text-[#6B6B6B] mb-4">
              {filtered.length} à faire sur {tasks.length} au total — les actions qui vous sont confiées
            </p>

            {/* Filtres */}
            <div className="flex gap-3 mb-4 flex-wrap items-center">
              <Funnel size={16} className="text-[#6B6B6B]" />
              <select
                value={filterChantier}
                onChange={e => setFilterChantier(e.target.value)}
                className="px-3 py-2 border border-[#E0E0DB] bg-white text-sm focus:outline-none focus:border-[#1E3A5F]"
                data-testid="mes-taches-filter-chantier"
              >
                <option value="">Tous les chantiers ({chantiersUnique.length})</option>
                {chantiersUnique.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select
                value={filterEcheance}
                onChange={e => setFilterEcheance(e.target.value)}
                className="px-3 py-2 border border-[#E0E0DB] bg-white text-sm focus:outline-none focus:border-[#1E3A5F]"
                data-testid="mes-taches-filter-echeance"
              >
                <option value="all">Toutes les échéances</option>
                <option value="overdue">En retard</option>
                <option value="week">Cette semaine</option>
                <option value="month">Ce mois</option>
              </select>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-10 border border-[#E0E0DB] bg-white" data-testid="mes-taches-empty">
                <p className="text-[#6B6B6B] text-sm">Aucune action ne correspond aux filtres actuels</p>
              </div>
            ) : (
              <div className="border border-[#E0E0DB] bg-white overflow-x-auto" data-testid="mes-taches-list">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF8] border-b border-[#E0E0DB]">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] uppercase text-xs tracking-wider">Chantier</th>
                      <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] uppercase text-xs tracking-wider">Type</th>
                      <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] uppercase text-xs tracking-wider">Action</th>
                      <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] uppercase text-xs tracking-wider">Échéance</th>
                      <th className="text-left py-3 px-4 font-semibold text-[#6B6B6B] uppercase text-xs tracking-wider">Priorité</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F0F0EC]">
                    {filtered.map(t => {
                      const echeanceDate = t.echeance ? new Date(t.echeance) : null;
                      const isOverdue = echeanceDate && echeanceDate < new Date();
                      return (
                        <tr
                          key={`${t.source_table}-${t.id}`}
                          // CHA-16 / A6 — une action de consultation porte son propre lien
                          // (appel d'offres, avis AMO) ; sinon Chantier › Actions.
                          onClick={() => navigate(t.lien || `/chantiers/${t.chantier_id}?tab=chantier&sub=actions`)}
                          className={`hover:bg-[#FDF6EC] cursor-pointer ${isOverdue ? 'bg-red-50' : ''}`}
                          data-testid={`mes-taches-row-${t.id}`}
                        >
                          <td className="py-3 px-4">
                            <div className="font-medium text-[#1E3A5F]">{t.chantier_name || '—'}</div>
                            {t.chantier_address && (
                              <div className="text-xs text-[#9A9A9A] flex items-center gap-1 mt-0.5">
                                <MapPin size={10} /> {t.chantier_address}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className="inline-block px-2 py-0.5 bg-[#F2F2F0] text-[#6B6B6B] text-xs uppercase tracking-wide">
                              {TYPE_LABELS[t.type] || t.type_libelle || t.type || '—'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium">{t.titre || '(sans titre)'}</div>
                            {t.source_table === 'consultation' && t.description && (
                              <div className="text-xs text-[#4B3A6B] mt-0.5">{t.description}</div>
                            )}
                            {t.entreprise && (
                              <div className="text-xs text-[#6B6B6B] mt-0.5">→ {t.entreprise}</div>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {echeanceDate ? (
                              <span className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                                {echeanceDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })}
                                {isOverdue && ' ⚠'}
                              </span>
                            ) : <span className="text-[#9A9A9A]">—</span>}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-block px-2 py-0.5 text-xs uppercase tracking-wide border ${PRIORITE_COLORS[t.priorite] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                              {PRIORITE_LABELS[t.priorite || 'normal'] || t.priorite}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Client, AMO, entreprise sans action ouverte : une phrase, pas une page vide. */}
        {rienDuTout && (
          <div className="border border-[#E0E0DB] bg-white text-center py-10 text-sm text-[#6B6B6B]" data-testid="ma-semaine-rien-a-faire">
            Rien ne vous attend cette semaine.
          </div>
        )}
      </div>
    </Layout>
  );
}
